import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { useNotif } from '@/common/notif/NotifContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { createContext, useContext, useEffect, useState } from 'react';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';
import { Mode } from '../enumerations/Mode';

// Default to nothing if EntityList is opened in a Dialog
const EntityMgrContext = createContext({
  entityDef: {},
  setExportReq: () => {},
});

export const EntityMgrProvider = ({ listComponent, children }) => {
  const rsrc = useRsrc();
  const { navState, mode } = useNavState();
  const { principal } = usePrincipal();
  const { notifs } = useNotif();

  const module = useModuleFromRoute();
  const entity = useEntityFromRoute();

  const [entityDef, setEntityDef] = useState();
  const [entityObj, setEntityObj] = useState({});
  const [hist, setHist] = useNavStateSync('hist', false);
  const [asOfDate, setAsOfDate] = useNavStateSync('asOfDate');
  const [searchBoxQuery, setSearchBoxQuery] = useNavStateSync('searchBoxQuery', '');
  const [exportReq, setExportReq] = useState();
  const [jobs, setJobs] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState();
  const [entityFormMode, setEntityFormMode] = useState(() => {
    if (mode !== Mode.DETAIL) return null;
    return navState.entityId ? EntityFormMode.VIEW : EntityFormMode.ADD;
  });

  // Fetch the EntityDef
  useEffect(() => {
    // Reset while loading new EntityDef to avoid race condition
    // setEntityDef(null); // Causes state issues with edit mode if set to null here
    rsrc.entityDef(module, entity).then((res) => {
      setEntityDef(res);
    });
  }, [module, entity]);

  // Fetch jobs
  useEffect(() => {
    const rowsReq = {
      query: 'JOB_FETCH',
      expr: ExprUtils.and(
        ExprUtils.whereParams('module', module, 'entity', entity, 'entityId', entityObj.id, 'lastRunStatus', 'RUN'),
        ExprUtils.isNot('cat', 'APPR'), // Approval Jobs already have a banner based on Entity status
        ExprUtils.or(
          ExprUtils.whereParams('cat', 'EXPORT', 'recBy', principal.username), // For Exports, we only care about the current user
          ExprUtils.isNot('cat', 'EXPORT'),
        ),
      ),
    };

    rsrc.rows('skdr', 'JOB', rowsReq).then((jobs) => {
      setJobs(jobs);
    });
  }, [entityObj.id]);

  // Monitor notifications that pertain to this Entity
  useEffect(() => {
    if (!notifs || notifs.length === 0) return;

    setJobs((prevJobs) => {
      let updatedJobs = [...prevJobs];

      // Add notif.event.job if it matches the criteria
      notifs.forEach((notif) => {
        const job = notif.event?.job;
        if (
          job &&
          job.module === module &&
          job.entity === entity &&
          (!entityObj.id || job.entityId === entityObj.id) &&
          !updatedJobs.some((j) => j.id === job.id) &&
          job.cat !== 'APPR'
        ) {
          updatedJobs.push(notif.event.job);
        }
      });

      // Remove jobs for which any notification has an eventType starting with "FINISH"
      // and a matching job id.
      updatedJobs = updatedJobs.filter((j) => {
        let jobCompleted = false;
        notifs.forEach((notif) => {
          const job = notif.event?.job;
          if (job && job.id === j.id && notif.eventType.startsWith('FINISH')) {
            jobCompleted = true;
          }
        });
        return !jobCompleted;
      });
      return updatedJobs;
    });
  }, [notifs]);

  const toggleHist = () => {
    setHist((prev) => !prev);
    setAsOfDate(null);
  };

  // Only render children once the entityDef is available
  if (!entityDef) {
    return <></>;
  }

  const value = {
    entityDef,
    entityObj,
    setEntityObj,
    asOfDate,
    setAsOfDate,
    hist,
    toggleHist,
    searchBoxQuery,
    setSearchBoxQuery,
    exportReq,
    setExportReq,
    jobs,
    breadcrumbs,
    setBreadcrumbs,
    entityFormMode,
    setEntityFormMode,
    listComponent, // Exposed for the EntityDetail in hist mode
  };

  return <EntityMgrContext.Provider value={value}>{children}</EntityMgrContext.Provider>;
};

export const useEntityMgr = () => useContext(EntityMgrContext);
